import React, {
  cloneElement,
  useCallback,
  useMemo,
  useState,
  ComponentType,
  forwardRef,
  useImperativeHandle,
  ReactNode,
  useEffect,
} from 'react';

import { Step, StepperContainer } from './Steps.styled';

export type StepItem = {
  id: string;
  component: ReactNode;
  metaData?: Object;
};

export type StepsProps = {
  steps: StepItem[];
  onStepChange?: (step: number) => void;
  extraProps?: { [key: string]: any };
};

export type Stepper = {
  prevStep: () => void;
  nextStep: () => void;
  toStep: (step: number, props?: any) => void;
  toStepById: (id: string, props?: any) => void;
  activeStep: number;
  firstStep: boolean;
  lastStep: boolean;
};

export type StepperProps<P> = P & {
  stepper: Stepper;
};

const DURATION = 300;

export function withStepperProps<P>(Component: ComponentType<StepperProps<P>>) {
  return function returnedComponent(props: any) {
    return <Component {...props} />;
  };
}

export const Steps = forwardRef(({ steps, onStepChange, extraProps }: StepsProps, ref) => {
  const [activeStep, setActiveStep] = useState(0);
  const [animation, setAnimation] = useState<string>();
  const [history, setHistory] = useState<number[]>([0]);
  const [contextProps, setContextProps] = useState<any>({});

  const changeStep = (step: number) => {
    const toNextStep = activeStep < step;

    setAnimation(toNextStep ? 'slideLeftExit' : 'slideRightExit');

    setTimeout(() => {
      setActiveStep(step);
      setAnimation(toNextStep ? 'slideLeft' : 'slideRight');
      setTimeout(() => {
        setAnimation(toNextStep ? 'slideLeftEnter' : 'slideRightEnter');
      }, 100);
    }, DURATION);
  };

  const handleNextStep = () => {
    if (activeStep === steps.length - 1) return;
    changeStep(activeStep + 1);
    setHistory([...history, activeStep + 1]);
  };

  const handleToStep = (step: number, props?: any) => {
    if (step !== activeStep) {
      setContextProps(props);
      changeStep(step);
      setHistory([...history, step]);
    }
  };

  const handleToStepById = (id: string, props?: any) => {
    const step = steps.findIndex((step) => step.id === id);

    if (step !== activeStep) {
      setContextProps(props);
      changeStep(step);
      setHistory([...history, step]);
    }
  };

  const handlePrevStep = () => {
    if (activeStep === 0) return;
    const prevStep = history[history.length - 2];
    setHistory(history.slice(0, -1));
    changeStep(prevStep);
  };

  const componentWithProps = useCallback(
    (component, props?: any) => component && cloneElement(component, props),
    [cloneElement],
  );

  const stepper = useMemo(
    () => ({
      prevStep: handlePrevStep,
      nextStep: handleNextStep,
      toStep: handleToStep,
      toStepById: handleToStepById,
      firstStep: activeStep === 0,
      lastStep: activeStep === steps.length - 1,
      activeStep,
    }),
    [handlePrevStep, handleNextStep, activeStep],
  );

  useEffect(() => {
    onStepChange?.(activeStep);
  }, [activeStep]);

  useImperativeHandle(ref, () => stepper);

  return (
    <StepperContainer>
      <Step duration={DURATION} className={animation}>
        {componentWithProps(steps[activeStep]?.component, {
          stepper,
          ...extraProps,
          ...contextProps,
        })}
      </Step>
    </StepperContainer>
  );
});
